package org.example.project.components

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.example.project.CheckTaxInfoResult
import org.example.project.FirebaseManager
import org.example.project.UpdateTaxInfoResult
import org.example.project.PrimaryColor

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ManageTaxInfoSection(taxCountriesList: List<String>) {
    val scope = rememberCoroutineScope()
    var taxCheckEmail by remember { mutableStateOf("") }
    var taxCountry by remember { mutableStateOf("") }
    var taxId by remember { mutableStateOf("") }
    var isCheckingTax by remember { mutableStateOf(false) }
    var isUpdatingTax by remember { mutableStateOf(false) }
    var taxMessage by remember { mutableStateOf<String?>(null) }
    var showTaxForm by remember { mutableStateOf(false) }
    var taxCountryExpanded by remember { mutableStateOf(false) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Billing Configuration",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))

            OutlinedTextField(
                value = taxCheckEmail,
                onValueChange = { 
                    taxCheckEmail = it 
                    showTaxForm = false 
                    taxMessage = null 
                },
                label = { Text("Account Email") },
                placeholder = { Text("partner@example.com") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            
            Spacer(modifier = Modifier.height(12.dp))
            
            Button(
                onClick = {
                    scope.launch {
                        isCheckingTax = true
                        taxMessage = null
                        val result = FirebaseManager.checkTaxInfo(taxCheckEmail)
                        when (result) {
                            is CheckTaxInfoResult.Success -> {
                                taxCountry = result.data.tax_country ?: ""
                                taxId = result.data.tax_id ?: ""
                                showTaxForm = true
                            }
                            is CheckTaxInfoResult.Error -> {
                                taxMessage = "Error: ${result.message} (You can still set the info below)"
                                taxCountry = ""
                                taxId = ""
                                showTaxForm = true
                            }
                        }
                        isCheckingTax = false
                    }
                },
                enabled = taxCheckEmail.isNotBlank() && !isCheckingTax && !isUpdatingTax,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                shape = MaterialTheme.shapes.medium
            ) {
                if (isCheckingTax) {
                    CircularProgressIndicator(modifier = Modifier.size(20.dp), color = Color.White, strokeWidth = 2.dp)
                } else {
                    Text("Lookup Billing", fontWeight = FontWeight.Bold)
                }
            }

            if (showTaxForm) {
                Spacer(modifier = Modifier.height(20.dp))
                HorizontalDivider(color = Color(0xFFF1F5F9))
                Spacer(modifier = Modifier.height(20.dp))

                ExposedDropdownMenuBox(
                    expanded = taxCountryExpanded,
                    onExpandedChange = { taxCountryExpanded = !taxCountryExpanded }
                ) {
                    OutlinedTextField(
                        value = taxCountry,
                        onValueChange = { taxCountry = it },
                        readOnly = false,
                        label = { Text("Region") },
                        placeholder = { Text("Select country") },
                        modifier = Modifier.fillMaxWidth().menuAnchor(MenuAnchorType.PrimaryEditable, true),
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = taxCountryExpanded) },
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = PrimaryColor,
                            unfocusedBorderColor = Color(0xFFE2E8F0)
                        ),
                        singleLine = true
                    )
                    ExposedDropdownMenu(
                        expanded = taxCountryExpanded,
                        onDismissRequest = { taxCountryExpanded = false }
                    ) {
                        taxCountriesList.forEach { country ->
                            DropdownMenuItem(
                                text = { Text(country) },
                                onClick = {
                                    taxCountry = country
                                    taxCountryExpanded = false
                                }
                            )
                        }
                    }
                }
                
                Spacer(modifier = Modifier.height(12.dp))
                
                OutlinedTextField(
                    value = taxId,
                    onValueChange = { taxId = it },
                    label = { Text("Tax / VAT ID") },
                    placeholder = { Text("Registration number") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PrimaryColor,
                        unfocusedBorderColor = Color(0xFFE2E8F0)
                    )
                )

                Spacer(modifier = Modifier.height(16.dp))

                Button(
                    onClick = {
                        scope.launch {
                            isUpdatingTax = true
                            taxMessage = null
                            val result = FirebaseManager.updateTaxInfo(taxCheckEmail, taxCountry, taxId)
                            when (result) {
                                is UpdateTaxInfoResult.Success -> {
                                    taxMessage = "Success: Billing info updated!"
                                }
                                is UpdateTaxInfoResult.Error -> {
                                    taxMessage = "Error: ${result.message}"
                                }
                            }
                            isUpdatingTax = false
                        }
                    },
                    enabled = !isUpdatingTax && taxCountry.isNotBlank() && taxId.isNotBlank(),
                    modifier = Modifier.fillMaxWidth(),
                    colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                    shape = MaterialTheme.shapes.medium
                ) {
                    if (isUpdatingTax) {
                        CircularProgressIndicator(modifier = Modifier.size(20.dp), color = Color.White, strokeWidth = 2.dp)
                    } else {
                        Text("Save Changes", fontWeight = FontWeight.Bold)
                    }
                }
            }

            if (taxMessage != null) {
                val isError = taxMessage!!.startsWith("Error")
                Spacer(modifier = Modifier.height(16.dp))
                Surface(
                    color = if (isError) Color(0xFFFEF2F2) else Color(0xFFF0FDF4),
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = taxMessage!!,
                            color = if (isError) Color(0xFFDC2626) else Color(0xFF16A34A),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }
}
